import React, { useEffect } from 'react';
import styled from "styled-components";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { selectCalendarDate } from '@shared/store/dashboard.duck';
import { selectCurrentUser, selectIconPack } from '@shared/store/auth.duck';
import { UserAvatarAndName } from '@shared/ui/molecules/UserAvatarAndName';
import {
  MemberMovements,
  TagWithMovements,
  TeamWithMovements,
} from '@features/whereabouts/models/movements.models';
import {
  loadCurrentUsersWhereabouts,
  selectCurrentUsersWhereabouts,
  selectIsWhereaboutsLoading,
  selectWhereabouts,
} from "@features/whereabouts/pages/team-movements/models/team-movements.duck";
import { UNKNOWN_OPTION } from '@features/whereabouts/services/WhereaboutOptions';
import { BodyRegular } from '@shared/ui/atoms/fonts/Body';
import Colours from '@shared/ui/atoms/Colours';
import { SimpleSpinner } from '@shared/ui/atoms/SimpleSpinner';
import { useTranslation } from "react-i18next";
import { WithDaysInWorkWeek } from "@shared/hooks/WithDaysInWorkWeek";
import { WithWorkPlannerTeamEvents } from '@features/wall-planner/hooks/WithWorkPlannerTeamEvents';
import { TeamEventsRow } from '@features/wall-planner/components/TeamEventsRow';
import { TeamMultiSelectWithFavourites } from '@shared/ui/organisms/team-drop-down-list/TeamMultiSelectWithFavourites';
import { selectAllTeams } from '@shared/store/config.duck';
import { selectAllTags } from '@shared/store/userTags.duck';
import { selectDailyFilter } from '@features/whereabouts/store/whereaboutsView.duck';
import { useOfficeNameMap } from '@features/whereabouts/hooks/useOfficeNameMap';
import { useWhereaboutsOptionResolver } from '@features/whereabouts/hooks/useWhereaboutsOptionResolver';
import { useScopedGroups } from '@features/whereabouts/hooks/useScopedGroups';
import { useScopedWeeklyWhereaboutsLoader } from '@features/whereabouts/hooks/useScopedWeeklyWhereaboutsLoader';
import { excludeUser } from '@features/whereabouts/models/whereaboutsScope';
import EmptyTeamView from '@shared/ui/organisms/movements-wallchart/components/empty-team-view/EmptyTeamView';
import EmptyTagView from '@shared/ui/organisms/movements-wallchart/components/empty-team-view/EmptyTagView';
import { useWhereaboutsScope } from './WhereaboutsScopeContext';
import { matchesDailyFilter } from './whereaboutsFilters';
import { buildSpans } from './weeklySpans';
import { CheckinConfirmationsContext, useLoadCheckinConfirmations } from './CheckinConfirmations';
import { MemberWeekRow } from './MemberWeekRow';
import { FilteredGroupEmptyMessage, WhereaboutsGroupSection } from './WhereaboutsGroupSection';
import {
  ColumnBackgrounds,
  ColumnBg,
  Container,
  ContentLayer,
  EmptyState,
  TeamHeader,
  TeamSection,
  TeamSelectSection,
  V2Colours,
} from "@features/whereabouts/pages/whereabouts-v2/WhereaboutsV2Styles";

export function WeeklyCalendarView() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { scope, isScoped } = useWhereaboutsScope();
  const user = useSelector(selectCurrentUser);
  const calendarDate = useSelector(selectCalendarDate);
  const filter = useSelector(selectDailyFilter);
  const iconPack = useSelector(selectIconPack);
  const allTeams = useSelector(selectAllTeams);
  const workGroups = useSelector(selectAllTags);
  const daysInWeek = WithDaysInWorkWeek();
  const scopedGroups = useScopedGroups(scope);
  WithWorkPlannerTeamEvents(scopedGroups.teams);

  const officeNameMap = useOfficeNameMap();
  const checkins = useLoadCheckinConfirmations(daysInWeek);
  const getOption = useWhereaboutsOptionResolver();

  const { teams, workgroups } = useSelector(selectWhereabouts);
  const loading = useSelector(selectIsWhereaboutsLoading);
  const currentUserData = useSelector(selectCurrentUsersWhereabouts);

  useEffect(() => {
    if (!calendarDate || !user?.id) return;
    dispatch(loadCurrentUsersWhereabouts({ calendarDate }));
  }, [dispatch, calendarDate, user?.id]);

  useScopedWeeklyWhereaboutsLoader(scope);

  const dayCount = daysInWeek.length;
  const hasTeamData = teams.length > 0 || workgroups.length > 0;

  const renderMembers = (members: MemberMovements[], showWhereaboutsLabel?: boolean, applyFilter: boolean = true) =>
    members.filter(Boolean).map(member => {
      const allSpans = buildSpans(member, daysInWeek, getOption, date => !!checkins(member.id, date));
      const spans = applyFilter ? allSpans.filter(s => matchesDailyFilter(filter, s.amOption, s.pmOption)) : allSpans;
      if (applyFilter && filter && spans.length === 0) return null;
      const hasAnyWhereabouts = allSpans.some(s => !(s.amOption.id === UNKNOWN_OPTION.id && s.pmOption.id === UNKNOWN_OPTION.id));
      return (
        <MemberWeekRow key={member.id}
                       member={member}
                       spans={spans}
                       hasAnyWhereabouts={hasAnyWhereabouts}
                       daysInWeek={daysInWeek}
                       officeNameMap={officeNameMap}
                       iconPack={iconPack}
                       showWhereaboutsLabel={showWhereaboutsLabel} />
      );
    });

  // The signed-in user has their own section at the top, so they are left out
  // of every group list.
  const renderTeamBody = (members: MemberMovements[]) => {
    const rendered = renderMembers(excludeUser(members, user?.id));
    if (filter && rendered.filter(Boolean).length === 0) {
      return <FilteredGroupEmptyMessage period="week" />;
    }
    return rendered;
  };

  const renderEmptyState = () => {
    if (!isScoped) {
      return <EmptyState><BodyRegular>{t('whereabouts-v2.no-groups-selected')}</BodyRegular></EmptyState>;
    }
    if (loading) {
      return <LoadingContainer><SimpleSpinner size={32} /></LoadingContainer>;
    }
    return <EmptyState><BodyRegular>{t('whereabouts-v2.group-not-found')}</BodyRegular></EmptyState>;
  };

  return (
    <CheckinConfirmationsContext.Provider value={checkins}>
      <Container>
        <ColumnBackgrounds $columns={dayCount}>
          {daysInWeek.map((_, i) => (
            <ColumnBg key={i} $even={i % 2 === 0} $isLast={i === daysInWeek.length - 1} />
          ))}
        </ColumnBackgrounds>

        <ContentLayer>
          <DayHeadersRow $dayCount={dayCount}>
            {daysInWeek.map((day, i) => {
              const today = moment();
              const isPast = day.isBefore(today, 'day');
              const isToday = day.isSame(today, 'day');
              const colour = isPast ? Colours.darkGrey : Colours.black;
              return (
                <DayHeader key={i} $isToday={isToday}>
                  <BodyRegular weight={600} colour={colour}>{day.format('ddd')}</BodyRegular>
                  <BodyRegular weight={600} colour={colour}>{day.format('D')}</BodyRegular>
                </DayHeader>
              );
            })}
          </DayHeadersRow>

          <TeamSection>
            <TeamHeader style={{paddingTop: 0, paddingBottom: 0}}>
              <UserAvatarAndName firstName={user?.firstname ?? ''} lastName={user?.lastname ?? ''} colour="blue" showEditLink={true} />
            </TeamHeader>
            {currentUserData && renderMembers([currentUserData], true, false)}
          </TeamSection>

          {!isScoped && (
            <TeamSelectSection>
              <TeamMultiSelectWithFavourites teams={allTeams}
                                             workGroups={workGroups}
                                             outerClassName={'multiSelect__wrapper'}
                                             multiSelectClassName={'multiSelect__dropDown'}
                                             reloadTeamWhereaboutsOnSelect={true} />
            </TeamSelectSection>
          )}

          {hasTeamData ? (
            <>
              {teams.map((team: TeamWithMovements) => (
                <WhereaboutsGroupSection key={team.team.id} title={team.team.name} restricted={team.restricted}>
                  <TeamEventsRow teamId={team.team.id} days={daysInWeek} showHeading={true} rowHeight={42} />
                  {isScoped && (team.movements ?? []).length === 0
                    ? <EmptyTeamView team={team.team} />
                    : renderTeamBody(team.movements || [])}
                </WhereaboutsGroupSection>
              ))}

              {workgroups.map((wg: TagWithMovements) => (
                <WhereaboutsGroupSection key={wg.tag.id} title={wg.tag.label} restricted={wg.restricted}>
                  {isScoped && (wg.movements ?? []).length === 0
                    ? <EmptyTagView tag={wg.tag} />
                    : renderTeamBody(wg.movements || [])}
                </WhereaboutsGroupSection>
              ))}
            </>
          ) : renderEmptyState()}
        </ContentLayer>
      </Container>
    </CheckinConfirmationsContext.Provider>
  );
}

const DayHeadersRow = styled.div<{ $dayCount: number }>`
  display: grid;
  grid-template-columns: repeat(${props => props.$dayCount}, 1fr);
  position: sticky;
  top: 0;
  z-index: 3;
  background: white;
`;

const DayHeader = styled.div<{ $isToday?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 5px 4px 7px 4px;
  border-bottom: 5px solid ${props => props.$isToday ? V2Colours.indigo : Colours.lightGrey};
  background: white;
  p {
    line-height: normal;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 48px;
`;
